import React, { useState, useEffect } from "react";
import { Bar, Doughnut } from "react-chartjs-2";
import { formatDateToYYYYMMDD } from "../helpers/DateHelpers";
import "../css/DashboardMain.css";
import SMSRecevied from "../../src/assets/Images/smsReceived.png";
import SMSPending from "../../src/assets/Images/SMSPending.png";
import SMSRejected from "../../src/assets/Images/SMSRejected.png";
import SMSTotal from "../../src/assets/Images/SMSTotal.png";
import { axiosPostApi } from "../../src/helpers/interceptorServices";
import { apiList } from "../../src/helpers/apiList";
import { MdHistory } from "react-icons/md";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import Loader from "./Loader/Loader";
import { useDispatch } from "react-redux";
import {
  setBalance,
  setCustomerSummaryData,
} from "./store/balance/balanceSlice";
import AIDashboard from "./ai-dashboard/AIDashboard";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend
);

const DashboardMain = () => {
  const [selected, setSelected] = useState("Last 30 days");
  const optionSelect = ["Last 30 days", "Last 7 days", "Last 90 days"];
  const [selectedRange, setSelectedRange] = useState("Last 30 days");
  const [overallProgressData, setOverallProgressData] = useState(null);
  const [smsOverviewData, setSmsOverviewData] = useState(null);
  const [customerSummary, setCustomerSummary] = useState([]);
  const loggedInUserName = sessionStorage.getItem("username");
  const [loading, setLoading] = useState(true);
  const [chartData1, setChartData1] = useState({
    labels: [],
    datasets: [
      { label: "Received", backgroundColor: "#4caf50", data: [] },
      { label: "Sent", backgroundColor: "#00b6ce", data: [] },
      { label: "Rejected", backgroundColor: "#e74c3c", data: [] },
    ],
  });

  const dispatch = useDispatch();
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    fetchDashboardData();
  }, [selectedRange]);

  const calculateStartDate = () => {
    const today = new Date();
    let startDate;

    switch (selectedRange) {
      case "Last 7 days":
        startDate = new Date(today.setDate(today.getDate() - 7));
        break;
      case "Last 30 days":
        startDate = new Date(today.setDate(today.getDate() - 30));
        break;
      case "Last 90 days":
        startDate = new Date(today.setDate(today.getDate() - 90));
        break;
      default:
        startDate = new Date(today.setDate(today.getDate() - 30)); // Default to Last 30 days
    }

    return formatDateToYYYYMMDD(startDate); // Format date to YYYY-MM-DD
  };

  const chartData = {
    labels: [
      "Jan 2024",
      "Feb 2024",
      "March 2024",
      "April 2024",
      "May 2024",
      "June 2024",
      "July 2024",
    ],
    datasets: [
      {
        label: "Received",
        backgroundColor: "#f1c40f", // Use yellow for received
        borderRadius: 10, // Rounded corners for the bars
        data: [12000, 19000, 3000, 5000, 20000, 30000, 15000],
        stack: "Stack 0",
      },
      {
        label: "Sent",
        backgroundColor: "#00b6ce", // Use blue for sent
        borderRadius: 10, // Rounded corners for the bars
        data: [5000, 10000, 5000, 4000, 10000, 15000, 10000],
        stack: "Stack 0",
      },
      {
        label: "Rejected",
        backgroundColor: "#e74c3c", // Use red for rejected
        borderRadius: 10, // Rounded corners for the bars
        data: [3000, 2000, 1500, 2000, 3000, 4000, 2000],
        stack: "Stack 0",
      },
    ],
  };

  const options1 = {
    responsive: true,
    maintainAspectRatio: false,
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom", // Legend at the bottom
      },
    },
    scales: {
      x: {
        stacked: true, // Stack bars on the x-axis
        grid: {
          display: false, // Remove gridlines on x-axis for cleaner look
        },
      },
      y: {
        stacked: true, // Stack bars on the y-axis
        beginAtZero: true,
        grid: {
          display: true,
          color: "#e0e0e0", // Subtle gridlines for a clean look
        },
      },
    },
  };

  const fetchDashboardData = () => {
    const startDate = calculateStartDate(); // Static start date
    const endDate = formatDateToYYYYMMDD(new Date()); // Today's date formatted
    const startDate1 = new Date(new Date().setDate(new Date().getDate() - 90));
    const finalStartDate = formatDateToYYYYMMDD(startDate1);

    const requestBody = {
      startDate: finalStartDate,
      endDate: endDate,
      username: loggedInUserName,
    };

    setLoading(true); // Set loading to true at the start

    axiosPostApi(apiList.DASHBOARD_DATA, requestBody)
      .then((response) => {
        if (response?.status === 200 && response?.data?.respCode === "00") {
          dispatch(setBalance(response?.data?.data?.balanceDetails));
          dispatch(
            setCustomerSummaryData(response?.data?.customerSummaryModel)
          );
          setSmsOverviewData(response?.data?.overview);
          setOverallProgressData(response?.data?.progressCircle);
          updateChartData(response?.data?.smsMonthlyData);
          setCustomerSummary(response?.data?.customerSummaryModel);
        } else {
          // Handle error case here if necessary
        }
      })
      .catch((error) => {
        console.error("Error fetching SMS overview data", error);
      })
      .finally(() => {
        setLoading(false); // Set loading to false once the request is complete
      });
  };

  const updateChartData = (apiData) => {
    const months = apiData.map((item) => item.monthName.substring(0, 3));
    const receivedSMS = apiData.map((item) => item.smsReceived);
    const pendingSMS = apiData.map((item) => item.smsPending);
    const rejectedSMS = apiData.map((item) => item.smsRejected);

    setChartData1({
      labels: months, // ['Jan', 'Feb', ...]
      datasets: [
        {
          label: "Received",
          backgroundColor: "#4caf50",
          data: receivedSMS,
        },
        {
          label: "Pending",
          backgroundColor: "#00b6ce",
          data: pendingSMS,
        },
        {
          label: "Failed",
          backgroundColor: "#e74c3c",
          data: rejectedSMS,
        },
      ],
    });
  };

  return (
    <>
      {loading && <Loader />}
      <div className="container">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <h3 className="mb-0 dashboard-title font-medium">Overview</h3>
          <div className="position-relative" style={{ width: "150px" }}>
            {/* <select className="form-select dashboard-dropdown" value={selected} onChange={(e) => setSelected(e.target.value)}>
            {optionSelect?.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select> */}
          </div>
        </div>

        {/* Overview Cards */}
        <div className="row mb-4">
          <div className="col-md-3">
            <div className="card p-3">
              <div className="d-flex align-items-center">
                <img
                  src={SMSRecevied}
                  alt="SMS Received"
                  className="me-3"
                  style={{ width: "40px", height: "40px" }}
                />
                <div>
                  <h5 className="mb-1 font-medium">SMS Received</h5>
                  <h3 className="font-medium text-[26px]">
                    {smsOverviewData
                      ? smsOverviewData.smsReceived
                      : "Loading..."}
                  </h3>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card p-3">
              <div className="d-flex align-items-center">
                <img
                  src={SMSRejected}
                  alt="SMS Rejected"
                  className="me-3"
                  style={{ width: "40px", height: "40px" }}
                />
                <div>
                  <h5 className="mb-1 font-medium">SMS Failed</h5>
                  <h3 className="font-medium text-[26px]">
                    {smsOverviewData
                      ? smsOverviewData.smsRejected
                      : "Loading..."}
                  </h3>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card p-3">
              <div className="d-flex align-items-center">
                <img
                  src={SMSPending}
                  alt="SMS Pending"
                  className="me-3"
                  style={{ width: "40px", height: "40px" }}
                />
                <div>
                  <h5 className="mb-1 font-medium">SMS Pending</h5>
                  <h3 className="font-medium text-[26px]">
                    {smsOverviewData
                      ? smsOverviewData.smsPending
                      : "Loading..."}
                  </h3>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card p-3">
              <div className="d-flex align-items-center">
                <img
                  src={SMSTotal}
                  alt="SMS Completed"
                  className="me-3"
                  style={{ width: "40px", height: "40px" }}
                />
                <div>
                  <h5 className="mb-1 font-medium">SMS Completed</h5>
                  <h3 className="font-medium text-[26px]">
                    {smsOverviewData
                      ? smsOverviewData.smsCompleted
                      : "Loading..."}
                  </h3>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="row mb-4">
          <div className="col-md-8">
            <div className="card p-3">
              <h5 className="font-medium text-[20px] mb-2">SMS Summary</h5>
              <div style={{ position: "relative", height: "250px" }}>
                <Bar data={chartData1} options={options1} />
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card p-3">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="font-medium text-[20px] mb-2">
                  Overall Progress
                </h5>
                {/* <div className="custom-select-wrapper">
                <select
                  value={selectedRange}
                  onChange={(e) => setSelectedRange(e.target.value)}
                  className="form-select custom-select custom-arrow"
                >
                  {optionSelect?.map(option => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div> */}
              </div>
              <div style={{ position: "relative", height: "250px" }}>
                {overallProgressData ? (
                  <Doughnut
                    data={{
                      labels: ["Completed", "Pending"],
                      datasets: [
                        {
                          data: [
                            overallProgressData?.smsCompleted,
                            overallProgressData?.smsRemaining,
                          ],
                          backgroundColor: ["#00b6ce", "#e0e0e0"],
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                    }}
                  />
                ) : (
                  "Loading..."
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Additional Charts Row */}
        {/* <div className="row mb-4">
        <div className="col-md-6">
          <div className="card p-3">
            <h5>BSNL SMS Summary</h5>
            <div className="chart-container">
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="card p-3">
            <h5>Onetel SMS Summary</h5>
            <div className="chart-container">
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div> */}

        {/* Summary Table */}
        <div className="row">
          <div className="col-12">
            <div
              className="card"
              style={{
                maxHeight: "300px",
                overflowY: "auto",
                paddingRight: "16px",
                paddingLeft: "16px",
                paddingBottom: "16px",
              }}
            >
              <h5
                style={{ marginTop: "16px" }}
                className="font-medium text-[20px] flex items-center gap-2 mb-2"
              >
                <MdHistory /> Today's customer summary
              </h5>
              <table className="table">
                <thead>
                  <tr>
                    <th>Customer Code</th>
                    <th>Customer Name</th>
                    <th>Opening Balance</th>
                    <th>Closing Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {customerSummary?.length > 0 ? (
                    customerSummary?.map((customer) => (
                      <tr key={customer?.customerCode}>
                        <td>{customer?.customerCode}</td>
                        <td>{customer?.name}</td>
                        <td>{customer?.openingBalance}</td>
                        <td>{customer?.closingBalance}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4">No customer data available</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DashboardMain;
